import { LoaderStore } from "@state/stores/common";
import QuickBetStore from "./QuickBetStore";
import MostPlayTicketsStore from "./MostPlayedTicketsStore";
import SharedBetSlipStore from "./SharedBetSlipStore";
import { action, computed, observable } from "mobx";
import { BetBuilderPicksStore } from "./BetBuilderPicksStore";
import { RootStore } from "@state/stores/types";
import { BetBoosterStore } from "./BetBoosterStore";
import BettingTipsStore from "./BettingTipsStore";
import { THEME_SETTINGS } from "@theme-settings";
import { BetBuilderApiService } from "@api-services/bet-builder/BetBuilderApiService";
import { QuickPlayApiService } from "@api-services/quick-play/QuickPlayApiService";

export default class QuickPlayStore {
	@observable activeHomeTab = QuickBetHomeTabs.NONE;
	@observable private _shouldDisplayHomeTabs = false;

	betBoosterStore: BetBoosterStore;
	betBuilderPicksStore: BetBuilderPicksStore;
	mostPlayedTicketsStore: MostPlayTicketsStore;
	public loaderStore: LoaderStore;
	quickBetStore: QuickBetStore;
	rootStore: RootStore;
	sharedBetSlipStore: SharedBetSlipStore;
	bettingTipsStore: BettingTipsStore;

	@computed get shouldShowLoader() {
		return this.loaderStore.isLoading || this.betBoosterStore.isLoading;
	}

	@computed get shouldShowEmptyState() {
		return (
			this.betBoosterStore.isInitialized &&
			!this.betBuilderPicksStore.BBPicks.size &&
			this.betBuilderPicksStore.isStoreInitialized
		);
	}

	@computed get shouldShowMostPlayedEmptyState() {
		return (
			this.betBoosterStore.isInitialized &&
			!this.mostPlayedTicketsStore.betSlips.size &&
			this.mostPlayedTicketsStore.isStoreInitialized
		);
	}

	@computed get isMostPlayAvailable() {
		return this.betBoosterStore.hubData.mostPlayedTicketIds?.length > 0;
	}

	@computed get isBetBuilderPicksAvailable() {
		return this.betBoosterStore.hubData.betBuilderPresetIds?.length > 0;
	}

	@computed get isBettingTipsAvailable() {
		return this.bettingTipsStore.bettingTips.length > 0;
	}

	@computed get shouldShowBettingTipsEmptyState() {
		return (
			this.bettingTipsStore.isStoreInitialized &&
			!this.bettingTipsStore.tips.size
		);
	}

	@computed public get shouldDisplayHomeTabs() {
		return this._shouldDisplayHomeTabs;
	}

	@computed public get activeTabSize() {
		switch (this.activeHomeTab) {
			case QuickBetHomeTabs.MOST_PLAYED: {
				return this.mostPlayedTicketsStore.betSlips.size;
			}
			case QuickBetHomeTabs.BETTING_TIPS:
				return this.bettingTipsStore.bettingTips.length;
			case QuickBetHomeTabs.BET_BUILDER_PICKS:
				return this.betBuilderPicksStore.BBPicks.size;
			default:
				return 0;
		}
	}
	constructor(rootStore: RootStore) {
		this.loaderStore = new LoaderStore();

		this.rootStore = rootStore;
		this.betBoosterStore = new BetBoosterStore(this);
		this.quickBetStore = new QuickBetStore(this);
		this.mostPlayedTicketsStore = new MostPlayTicketsStore(this);
		this.sharedBetSlipStore = new SharedBetSlipStore(this);
		this.betBuilderPicksStore = new BetBuilderPicksStore(this.rootStore);
		this.bettingTipsStore = new BettingTipsStore(this);
	}

	@action.bound
	async onInitialize() {
		this.loaderStore.suspend();
		if (this.rootStore.betBoosterHubStore.isSessionExpired) {
			this.rootStore.betBoosterHubStore.restartSession();
		}

		this.loaderStore.resume();

		if (
			THEME_SETTINGS.features.quickPlayOptions?.includes(
				"bet-builder-picks"
			) ||
			THEME_SETTINGS.features.quickPlayOptions?.includes(
				"most-played-events"
			)
		) {
			this.betBoosterStore.onInitialize();
		}
		if (THEME_SETTINGS.features.quickPlayOptions.includes("betting-tips")) {
			await this.bettingTipsStore.onInitialized();
		}

		let [isBBPicksAvailable, isMostPlayedTicketsAvailable] =
			await this.getAvailability();

		if (isBBPicksAvailable) {
			this.setShouldDisplayHomeTabs(true);
			this.setActiveHomeTabToBBPicks();
		} else if (isMostPlayedTicketsAvailable) {
			this.setShouldDisplayHomeTabs(true);
			this.setActiveHomeTabToMostPlayed();
		} else if (this.isBettingTipsAvailable) {
			this.setShouldDisplayHomeTabs(true);
			this.setActiveHomeTabToBettingTips();
		} else {
			this.setShouldDisplayHomeTabs(false);
		}
	}

	@action.bound
	async getAvailability() {
		let isBBPicksAvailable = false;
		let isMostPlayedTicketsAvailable = false;

		if (
			THEME_SETTINGS.features.quickPlayOptions?.includes(
				"bet-builder-picks"
			) ||
			THEME_SETTINGS.features.quickPlayOptions?.includes(
				"most-played-events"
			)
		) {
			try {
				const bbPicksResponse =
					await BetBuilderApiService.getBetBuilderPicksAvailability();
				if (
					bbPicksResponse.statusCode == 200 &&
					bbPicksResponse.response &&
					bbPicksResponse.response.length > 0
				) {
					isBBPicksAvailable = true;
				}
			} catch (e) {
				console.error(e);
			}
			try {
				const mostPlayedTicketsResponse =
					await QuickPlayApiService.getMostPlayedPicksAvailability();
				if (
					mostPlayedTicketsResponse.statusCode == 200 &&
					mostPlayedTicketsResponse.response &&
					mostPlayedTicketsResponse.response.length > 0
				) {
					isMostPlayedTicketsAvailable = true;
				}
			} catch (e) {
				console.error(e);
			}
		}

		return [isBBPicksAvailable, isMostPlayedTicketsAvailable];
	}

	@action.bound
	onDispose() {
		this.betBoosterStore.onDispose();
		this.betBuilderPicksStore.onDispose();
		this.mostPlayedTicketsStore.onDispose();
		this.bettingTipsStore.onDispose();

		this.setShouldDisplayHomeTabs(false);
		this.activeHomeTab = QuickBetHomeTabs.NONE;
	}

	@action.bound
	setActiveHomeTabToBBPicks() {
		if (this.isBetBuilderPicksAvailable) {
			this.setActiveHomeTab(QuickBetHomeTabs.BET_BUILDER_PICKS);
		}
	}

	@action.bound
	setActiveHomeTabToMostPlayed() {
		if (this.isMostPlayAvailable) {
			this.setActiveHomeTab(QuickBetHomeTabs.MOST_PLAYED);
		}
	}

	@action.bound
	setActiveHomeTabToBettingTips() {
		if (this.isBettingTipsAvailable) {
			this.setActiveHomeTab(QuickBetHomeTabs.BETTING_TIPS);
		}
	}

	@action.bound
	setActiveHomeTab(activeTo: QuickBetHomeTabs) {
		this.activeHomeTab = activeTo;
	}

	@action.bound
	setShouldDisplayHomeTabs(value: boolean) {
		this._shouldDisplayHomeTabs = value;
	}
}

export enum QuickBetHomeTabs {
	BET_BUILDER_PICKS = "BET_BUILDER_PICKS",
	MOST_PLAYED = "MOST_PLAYED",
	BETTING_TIPS = "BETTING_TIPS",
	NONE = "NONE",
}
